import { useCallback, useEffect, useRef, useState } from 'react';
import { contaApi, type ParticipantDocument } from './api.js';

const ACTIVE = new Set<ParticipantDocument['state']>([
  'not_requested',
  'queued',
  'processing',
  'retry_wait',
]);

/** Reconsulta apenas leitura; nunca cria um job por timeout ou falha de rede. */
export function useDocumentos(
  participationId: string | null,
  api: typeof contaApi,
  expected: string,
) {
  const [state, setState] = useState<{
    id: string | null;
    documents: readonly ParticipantDocument[];
    error: boolean;
  }>({ id: null, documents: [], error: false });
  const [refresh, setRefresh] = useState(0);
  const waiting = useRef(
    new Map<
      string,
      {
        participation: string;
        kind: string;
        format: string;
        baseline: string | undefined;
      }
    >(),
  );
  const afterRequest = useCallback(
    (kind: string, format: string, baseline: string | undefined) => {
      if (participationId)
        waiting.current.set(`${participationId}:${kind}:${format}`, {
          participation: participationId,
          kind,
          format,
          baseline,
        });
      setRefresh((value) => value + 1);
    },
    [participationId],
  );
  const sequence = useRef(0);
  const recheck = useCallback(() => setRefresh((value) => value + 1), []);
  useEffect(() => {
    if (participationId === null) return;
    const generation = ++sequence.current;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    const query = async () => {
      if (cancelled || document.visibilityState === 'hidden' || !navigator.onLine) return;
      try {
        const response = await api.documents(participationId);
        if (cancelled || generation !== sequence.current) return;
        if (!response.ok) {
          setState((current) => ({
            id: participationId,
            documents: current.id === participationId ? current.documents : [],
            error: true,
          }));
          if (
            response.problem.status === 401 ||
            response.problem.status === 403 ||
            response.problem.status === 410
          )
            return;
        } else {
          setState({ id: participationId, documents: response.value.documents, error: false });
          let waitForNewArtifact = false;
          for (const [key, awaited] of waiting.current) {
            if (awaited.participation !== participationId) {
              waiting.current.delete(key);
              continue;
            }
            const document = response.value.documents
              .filter(
                (item) => item.kind === awaited.kind && (item.format ?? 'a4') === awaited.format,
              )
              .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
            if (!document || document.id === awaited.baseline) waitForNewArtifact = true;
            else waiting.current.delete(key);
          }
          const kinds = expected.split(',');
          const latest = kinds.map(
            (kind) =>
              response.value.documents
                .filter((item) => item.kind === kind)
                .sort((a, b) => b.created_at.localeCompare(a.created_at))[0],
          );
          if (
            !waitForNewArtifact &&
            latest.every((item) => item !== undefined && !ACTIVE.has(item.state)) &&
            !response.value.documents.some((item) => ACTIVE.has(item.state))
          )
            return;
        }
      } catch {
        if (cancelled) return;
        setState((current) => ({
          id: participationId,
          documents: current.id === participationId ? current.documents : [],
          error: true,
        }));
      }
      attempts += 1;
      // Backoff limitado a um minuto enquanto houver trabalho; aba oculta/rede pausam acima.
      if (!cancelled)
        timer = setTimeout(
          () => {
            void query();
          },
          Math.min(60_000, 5000 * 2 ** Math.min(attempts - 1, 4)),
        );
    };
    const resume = () => {
      if (document.visibilityState === 'hidden' || !navigator.onLine) return;
      recheck();
    };
    void query();
    window.addEventListener('online', resume);
    window.addEventListener('focus', resume);
    document.addEventListener('visibilitychange', resume);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      window.removeEventListener('online', resume);
      window.removeEventListener('focus', resume);
      document.removeEventListener('visibilitychange', resume);
    };
  }, [participationId, api, expected, refresh, recheck]);
  return {
    documents: state.id === participationId ? state.documents : [],
    queryFailed: state.id === participationId && state.error,
    recheck,
    afterRequest,
  };
}
